import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { closeUserForm } from 'src/components/user/UserDetailsForm';
import { DEFAULT_LANGUAGE, LOCAL_STORAGE_KEYS } from 'src/contstant/commonLabels';
import { userService } from 'src/data/api/orbit-service/userService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  CheckUserPermissionsRequest,
  CheckUserPermissionsResponse,
  CreateUserRequest,
  DeleteUserRequest,
  GetUsersRequest,
  GetUserMetadataRequest,
  ListUsersRequest,
  UpdateUserRequest,
  User,
  UserMetadata,
  UserPermission,
} from 'src/types/orbit/User';
import { handleError } from 'src/utils/generalUtils';
import { saveToLocalStorage } from 'src/utils/localStorageUtils';
import { withRetry } from 'src/utils/retryUtils';

export interface UserState {
  users: User[];
  currentUser?: User;
  currentUserMetadata?: UserMetadata;
  userSelectedLanguage?: string;
  checkUserPermissionResponse: CheckUserPermissionsResponse | null;
  fetchUsersApiStatus: ApiRequestStatus;
  updateUserApiStatus: UserApiStatus;
  fetchUserByUserIdApiStatus: UserApiStatus;
  fetchUserMetadataApiStatus: ApiRequestStatus;
  deleteUserApiStatus: ApiRequestStatus;
  checkUserPermissionApiStatus: ApiRequestStatus;
}
interface UserApiStatus extends ApiRequestStatus {
  userId?: string;
}
const initialState: UserState = {
  users: [],
  checkUserPermissionResponse: null,
  fetchUsersApiStatus: initApiStatus,
  fetchUserByUserIdApiStatus: { ...initApiStatus, isSuccess: undefined },
  fetchUserMetadataApiStatus: initApiStatus,
  updateUserApiStatus: { ...initApiStatus },
  deleteUserApiStatus: initApiStatus,
  checkUserPermissionApiStatus: initApiStatus,
  userSelectedLanguage: DEFAULT_LANGUAGE,
};

export const fetchUsersApiThunk = createAsyncThunk(
  'users/fetchUsers',
  async (data: ListUsersRequest, { rejectWithValue }) => {
    try {
      const response = await withRetry(() => userService.getAll(data));
      return {
        ...response,
        isPaginating: !!data.nextToken,
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const fetchUsersByIDApiThunk = createAsyncThunk(
  'users/fetchUsersByID',
  async (data: GetUsersRequest, { rejectWithValue }) => {
    try {
      const response = await userService.getUserById(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const fetchUserMetadataApiThunk = createAsyncThunk(
  'users/fetchUserMetadata',
  async (data: GetUserMetadataRequest, { rejectWithValue }) => {
    try {
      const response = await userService.getUserMetadata(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

// Create async thunk for adding a new user
export const addUserApiThunk = createAsyncThunk(
  'users/addUser',
  async (data: CreateUserRequest, { rejectWithValue }) => {
    try {
      const response = await userService.create(data);
      return {
        user: { userPermission: response?.user },
        message: 'User created successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const checkUserPermissionApiThunk = createAsyncThunk(
  'users/checkUserPermission',
  async (data: CheckUserPermissionsRequest, { rejectWithValue }) => {
    try {
      if (!data.userId || !data.permissions || !data.programId) {
        throw new Error('Invalid data');
      }
      const response = await userService.checkUserPermission(data);

      return { data: response, message: '' };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

// Create async thunk for updating a user
export const updateUserApiThunk = createAsyncThunk(
  'users/updateUser',
  async ({ data }: { data: UpdateUserRequest }, { rejectWithValue }) => {
    try {
      const response = await userService.update(data);
      return {
        user: { userPermission: response?.user },
        message: 'User updated successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteUserApiThunk = createAsyncThunk(
  'users/deleteUser',
  async ({ data }: { data: DeleteUserRequest }, { rejectWithValue }) => {
    try {
      const response = await userService.delete(data);
      return { userId: response.userId, message: 'Delete user successfully' };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetAllApiStatus: (state) => {
      state.fetchUsersApiStatus = initApiStatus;
      state.updateUserApiStatus = initApiStatus;
      state.checkUserPermissionApiStatus = initApiStatus;
    },
    clearUsersNextToken: (state) => {
      state.fetchUsersApiStatus.nextToken = undefined;
    },
    removeUserFromState: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter(
        (bu) => bu.userPermission.userId !== action.payload,
      );
    },
    selectUserLanguageState: (state, action) => {
      state.userSelectedLanguage = action.payload;
    },
    // Add new reducer actions for user operations
    addUserToState: (state, action) => {
      state.users.push(action.payload);
    },
    updateUserInState: (state, action) => {
      const index = state.users.findIndex(
        (user) =>
          user.userPermission.userId === action.payload.user.userPermission.userId,
      );
      if (index !== -1) {
        const createdDate = state.users[index].userPermission.createdAt;
        const createdBy = state.users[index].userPermission.createdBy;
        const data = { ...state.users[index], ...action.payload.user };
        if (
          !action.payload.user.userPermission.createdAt ||
          !action.payload.user.userPermission.createdBy
        ) {
          data.userPermission.createdAt = createdDate;
          data.userPermission.createdBy = createdBy;
        }
        state.users[index] = data;
      }
    },

    clearAPIStatusMessageFromState: (state) => {
      state.deleteUserApiStatus.errorMessage = '';
      state.deleteUserApiStatus.successMessage = '';

      state.updateUserApiStatus.errorMessage = '';
      state.updateUserApiStatus.successMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersApiThunk.pending, (state, action) => {
        state.fetchUsersApiStatus.isLoading = true;
        state.fetchUsersApiStatus.errorMessage = null;
        // If no nextToken in the request, this is a fresh load - clear existing users
        if (action.meta?.arg && !action.meta.arg.nextToken) {
          state.users = [];
          state.fetchUsersApiStatus.nextToken = undefined;
          state.fetchUsersApiStatus.infoMessage = null;
        } else if (action.meta?.arg?.nextToken) {
          // Paginating - show info message
          state.fetchUsersApiStatus.infoMessage =
            'Processing is taking a bit longer than usual. Please stand by.';
        }
      })
      .addCase(fetchUsersApiThunk.fulfilled, (state, action) => {
        state.fetchUsersApiStatus.isLoading = false;
        state.fetchUsersApiStatus.isSuccess = true;
        state.fetchUsersApiStatus.infoMessage = null; // Clear info message when loaded
        // Append new users to existing list (supports pagination)
        state.users = [...state.users, ...action.payload.users];
        state.fetchUsersApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(fetchUsersApiThunk.rejected, (state, action) => {
        state.fetchUsersApiStatus.isLoading = false;
        state.fetchUsersApiStatus.isSuccess = false;
        state.fetchUsersApiStatus.infoMessage = null; // Clear info message on error
        state.fetchUsersApiStatus.nextToken = undefined; // Clear nextToken to prevent retry loop
        // Show a more helpful message if some users were already loaded (pagination failure)
        if (state.users.length > 0) {
          state.fetchUsersApiStatus.errorMessage =
            'An incomplete list of users has been loaded. Please refresh or try again later.';
        } else {
          state.fetchUsersApiStatus.errorMessage = action.payload as string;
        }
      });

    builder
      .addCase(fetchUsersByIDApiThunk.pending, (state) => {
        state.fetchUserByUserIdApiStatus.isLoading = true;
        state.fetchUserByUserIdApiStatus.errorMessage = null;
        state.fetchUserByUserIdApiStatus.isSuccess = false;
      })
      .addCase(fetchUsersByIDApiThunk.fulfilled, (state, action) => {
        state.fetchUserByUserIdApiStatus.isLoading = false;
        state.fetchUserByUserIdApiStatus.isSuccess = true;
        state.currentUser = action.payload.userDetails;
        saveToLocalStorage(LOCAL_STORAGE_KEYS.USER_INFO, state.currentUser);
      })
      .addCase(fetchUsersByIDApiThunk.rejected, (state, action) => {
        state.fetchUserByUserIdApiStatus.isLoading = false;
        state.fetchUserByUserIdApiStatus.isSuccess = false;
        state.currentUser = undefined;
        state.fetchUserByUserIdApiStatus.errorMessage = action.payload as string;
      });

    builder
      .addCase(fetchUserMetadataApiThunk.pending, (state) => {
        state.fetchUserMetadataApiStatus.isLoading = true;
        state.fetchUserMetadataApiStatus.errorMessage = null;
        state.fetchUserMetadataApiStatus.isSuccess = false;
      })
      .addCase(fetchUserMetadataApiThunk.fulfilled, (state, action) => {
        state.fetchUserMetadataApiStatus.isLoading = false;
        state.fetchUserMetadataApiStatus.isSuccess = true;
        state.currentUserMetadata = action.payload.userMetadata;
      })
      .addCase(fetchUserMetadataApiThunk.rejected, (state, action) => {
        state.fetchUserMetadataApiStatus.isLoading = false;
        state.fetchUserMetadataApiStatus.isSuccess = false;
        state.currentUserMetadata = undefined;
        state.fetchUserMetadataApiStatus.errorMessage = action.payload as string;
      });
    // Add other cases for createRole, updateRole, etc.

    // Add User cases
    builder
      .addCase(addUserApiThunk.pending, (state) => {
        state.updateUserApiStatus.isLoading = true;
        state.updateUserApiStatus.errorMessage = null;
        state.updateUserApiStatus.successMessage = null;
      })
      .addCase(addUserApiThunk.fulfilled, (state, action) => {
        state.updateUserApiStatus.isLoading = false;
        state.updateUserApiStatus.isSuccess = true;
        state.updateUserApiStatus.successMessage = action.payload.message;
        usersSlice.caseReducers.addUserToState(state, {
          type: 'addUserToState',
          payload: action.payload.user,
        });
        closeUserForm();
      })
      .addCase(addUserApiThunk.rejected, (state, action) => {
        state.updateUserApiStatus.isLoading = false;
        state.updateUserApiStatus.isSuccess = false;
        state.updateUserApiStatus.errorMessage = action.payload as string;
        state.updateUserApiStatus.successMessage = null;
      });

    // Update User cases
    builder
      .addCase(updateUserApiThunk.pending, (state, action) => {
        state.updateUserApiStatus.userId = action.meta.arg.data.userId;
        state.updateUserApiStatus.isLoading = true;
        state.updateUserApiStatus.errorMessage = null;
        state.updateUserApiStatus.successMessage = null;
      })
      .addCase(updateUserApiThunk.fulfilled, (state, action) => {
        state.updateUserApiStatus.isLoading = false;
        state.updateUserApiStatus.isSuccess = true;
        state.updateUserApiStatus.successMessage = action.payload.message;
        usersSlice.caseReducers.updateUserInState(state, {
          type: 'updateUserInState',
          payload: action.payload,
        });
        closeUserForm();
      })
      .addCase(updateUserApiThunk.rejected, (state, action) => {
        state.updateUserApiStatus.isLoading = false;
        state.updateUserApiStatus.isSuccess = false;
        state.updateUserApiStatus.errorMessage = action.payload as string;
        state.updateUserApiStatus.successMessage = null;
      });
    // Delete User cases
    builder
      .addCase(deleteUserApiThunk.pending, (state) => {
        state.deleteUserApiStatus.isLoading = true;
        state.deleteUserApiStatus.errorMessage = null;
        state.deleteUserApiStatus.successMessage = null;
      })
      .addCase(deleteUserApiThunk.fulfilled, (state, action) => {
        state.deleteUserApiStatus.isLoading = false;
        state.deleteUserApiStatus.isSuccess = true;
        state.deleteUserApiStatus.successMessage = action.payload.message;
        usersSlice.caseReducers.removeUserFromState(state, {
          type: 'removeUserFromState',
          payload: action.payload.userId,
        });
        closeUserForm();
      })
      .addCase(deleteUserApiThunk.rejected, (state, action) => {
        state.deleteUserApiStatus.isLoading = false;
        state.deleteUserApiStatus.isSuccess = false;
        state.deleteUserApiStatus.errorMessage = action.payload as string;
        state.deleteUserApiStatus.successMessage = null;
      });

    //Check user permission
    builder
      .addCase(checkUserPermissionApiThunk.pending, (state) => {
        state.checkUserPermissionApiStatus.isLoading = true;
        state.checkUserPermissionApiStatus.errorMessage = null;
        state.checkUserPermissionApiStatus.successMessage = null;
        state.checkUserPermissionResponse = null;
      })
      .addCase(checkUserPermissionApiThunk.fulfilled, (state, action) => {
        state.checkUserPermissionApiStatus.isLoading = false;
        state.checkUserPermissionApiStatus.errorMessage = null;
        state.checkUserPermissionApiStatus.successMessage = null;
        state.checkUserPermissionResponse = action.payload.data;
      })
      .addCase(checkUserPermissionApiThunk.rejected, (state, action) => {
        state.checkUserPermissionApiStatus.isLoading = false;
        state.checkUserPermissionApiStatus.isSuccess = false;
        state.checkUserPermissionApiStatus.errorMessage = action.payload as string;
        state.checkUserPermissionApiStatus.successMessage = null;
        state.checkUserPermissionResponse = null;
      });
  },
});

// Make sure the reducer explicitly handles undefined state
const usersReducer = (state: UserState = initialState, action: any) => {
  return usersSlice.reducer(state, action);
};
export const {
  updateUserInState,
  resetAllApiStatus,
  clearAPIStatusMessageFromState,
  removeUserFromState,
  selectUserLanguageState,
  clearUsersNextToken,
} = usersSlice.actions;
export default usersReducer;
